import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../api/client";
import logger from "../lib/logger";
import { Sentry, isProduction } from "../lib/sentry";

const AuthContext = createContext(null);

/**
 * Auth state values:
 *  - undefined: still checking session
 *  - null:      not authenticated
 *  - object:    authenticated user
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (isProduction && data) {
        Sentry.setUser({ id: data.id, email: data.email, role: data.role });
      }
    } catch (err) {
      // 401 just means there's no valid session cookie — treat as logged out.
      // Anything else is unexpected and worth surfacing in dev tools.
      if (err?.response?.status !== 401) {
        logger.error("Auth check failed:", err);
      }
      setUser(null);
      if (isProduction) Sentry.setUser(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    // Backend sets an httpOnly access_token cookie on success.
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    if (isProduction && data?.user) {
      Sentry.setUser({ id: data.user.id, email: data.user.email, role: data.user.role });
    }
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      logger.error("Logout request failed:", err);
    }
    setUser(null);
    if (isProduction) Sentry.setUser(null);
    // Hard redirect ensures we drop any in-memory state, force any
    // outstanding SSE / watch-position listeners to close, and side-step
    // any stale-closure / Toaster-overlay edge case in the React tree.
    window.location.replace("/login");
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, refresh }),
    [user, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
